import { Component } from '@theme/component';

/**
 * <cds-plan-picker> — the SELECT ORDER widget's one piece of behaviour (Made in Matcha).
 *
 * Its entire job is `select.disabled = !subscriptionRadio.checked`. A `<select>` always has a
 * selected option, so hiding the delivery schedule with CSS is not enough — it would still post a
 * `selling_plan` on a one-time purchase. Only the `disabled` attribute excludes a control from
 * `FormData`, and CSS cannot set an attribute. Everything else about the widget (which row looks
 * selected, whether the schedule is revealed, which add-to-cart price shows) is `:checked` and
 * `:has()` in `blocks/custom-selling-plan-picker.liquid` and `blocks/custom-buy-buttons.liquid`.
 *
 * WHY THIS IS A CUSTOM ELEMENT AND NOT THE INLINE `<script>` IT REPLACES. The quick-add drawer
 * builds its panel by `fetch`ing the PDP, parsing it with `DOMParser` and morphing the result into
 * the live document (`assets/quick-add.js`). A script parsed that way never runs when it is
 * inserted: the HTML spec's "prepare the script element" algorithm sets the element's
 * already-started flag (step 11) *before* it returns for scripting being disabled (step 14), so
 * the later insertion hits the already-started check on step 1 and does nothing. Custom elements
 * have the opposite behaviour — they upgrade on insertion, which is why `product-form-component`
 * and `variant-picker` work inside the drawer at all.
 *
 * Left as an inline script, the drawer would reveal the delivery schedule when the shopper chose
 * Subscription and then post no `selling_plan`, selling them a one-time item under a UI that said
 * otherwise. That failure is silent, which is why this moved.
 *
 * The element is defined globally from `snippets/custom.globals.liquid`, not beside its markup,
 * for the same reason: a `<script>` that arrives through morph is never executed, so the
 * definition has to already be on the page when the node lands. Same constraint as
 * <free-shipping-suggestions> (see docs/hotspots.md #2).
 *
 * IT STILL FAILS SAFE. The markup ships `disabled` whenever one-time is the pre-checked option, so
 * with no JavaScript at all — this module blocked, 404ing, or an unsupported browser — a one-time
 * purchase is still correct. The degradation is that subscription becomes unbuyable, never that a
 * shopper is silently put on a plan they did not choose.
 *
 * @extends {Component}
 */
class SellingPlanPicker extends Component {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    this.addEventListener('change', this.#sync, { signal: this.#controller.signal });
    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Re-runs the sync after a morph.
   *
   * `assets/morph.js:93` calls this on every `Component` whose subtree it changed, so a re-render
   * that swaps the markup out from under us cannot leave `disabled` and the radio out of step.
   * The listener itself survives — morph updates children, it does not replace this host — but the
   * *attribute* is part of the morphed markup and comes back from the server's idea of the state.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#sync();
  }

  get #select() {
    return /** @type {HTMLSelectElement | null} */ (this.querySelector('.cds-schedule__select'));
  }

  get #subscribeRadio() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('.cds-plan-row__input--sub'));
  }

  /** A disabled control is excluded from FormData; a hidden one is not. This is the whole job. */
  #sync = () => {
    const select = this.#select;
    const subscribe = this.#subscribeRadio;
    if (!select || !subscribe) return;

    select.disabled = !subscribe.checked;
  };
}

if (!customElements.get('cds-plan-picker')) {
  customElements.define('cds-plan-picker', SellingPlanPicker);
}
